import React, { useState, useEffect, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import Logo from '../assets/plannic.svg';
import { useSection } from '../context/SectionContext';

// const navItems = [
//   { label: 'Startseite', path: '/' },
//   { label: 'Leistungen', path: '/services' },
//   { label: 'Über uns', path: '/about-us' },
//   { label: 'Referenzen', path: '/references' },
//   { label: 'Kontakt', path: '/contact' }
// ];

const getNavItems = (section) => {
  if (section === 'architecture') {
    return [
      { label: 'Startseite', path: '/architecture' },
      { label: 'Leistungen', path: '/architecture/services' },
      { label: 'Über uns', path: '/about-us' },
      { label: 'Referenzen', path: '/architecture/references' },
      { label: 'Kontakt', path: '/contact' }
    ];
  }
  if (section === 'cad') {
    return [
      { label: 'Startseite', path: '/cad-services' },
      { label: 'Leistungen', path: '/cad-services/services' },
      { label: 'Über uns', path: '/about-us' },
      { label: 'Referenzen', path: '/cad-services/references' },
      { label: 'Kontakt', path: '/contact' }
    ];
  }
  return [
    { label: 'Startseite', path: '/' },
    { label: 'Leistungen', path: '/architecture/services' },
    { label: 'Über uns', path: '/about-us' },
    { label: 'Referenzen', path: '/architecture/references' },
    { label: 'Kontakt', path: '/contact' }
  ];
};

const MotionBox = motion(Box);

const Hamburger = ({ open, scrolled }) => (
  <MotionBox component='span' sx={{ width: 24, height: 18, display: 'inline-flex', flexDirection: 'column', justifyContent: 'space-between' }}>
    {[0, 1, 2].map((i) => (
      <MotionBox
        key={i}
        component='span'
        sx={{
          height: 2,
          borderRadius: 1,
          backgroundColor: scrolled ? 'black' : 'white',
          transition: 'background-color 0.3s ease-in-out'
        }}
        animate={
          open
            ? [
                { top: 9, rotate: i === 0 ? 45 : i === 2 ? -45 : 0, opacity: i === 1 ? 0 : 1 },
                { rotate: 0 } // fallback when closing
              ][0]
            : { top: i * 8, rotate: 0, opacity: 1 }
        }
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      />
    ))}
  </MotionBox>
);

const drawerVariants = {
  hidden: { opacity: 0, x: 20 },
  show: { opacity: 1, x: 0 }
};

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { section } = useSection();

  const navItems = useMemo(() => getNavItems(section), [section]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const activePath = location.pathname;

  const drawer = (
    <Box sx={{ width: 260, px: 2 }}>
      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <Link to='/' style={{ textDecoration: 'none' }}>
          <img src={Logo} alt='Logo' style={{ height: 40 }} />
        </Link>
      </Box>
      <Divider />
      <List sx={{ py: 1 }}>
        {navItems.map((item, i) => {
          const isActive = activePath === item.path;
          return (
            <motion.div
              key={item.label}
              variants={drawerVariants}
              initial='hidden'
              animate='show'
              transition={{ delay: 0.08 * i, duration: 0.25 }}
              whileTap={{ scale: 0.97 }}>
              <ListItem disablePadding sx={{ position: 'relative' }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={() => {
                    setMobileOpen(false);
                  }}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    px: 3,
                    py: 2,
                    borderRadius: 2,
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.03)'
                    }
                  }}>
                  {isActive && (
                    <motion.span
                      layoutId='drawer-active-highlight'
                      style={{
                        position: 'absolute',
                        inset: 4,
                        borderRadius: 12,
                        background: 'rgba(0, 0, 0, 0.05)',
                        pointerEvents: 'none'
                      }}
                      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                    />
                  )}
                  <ListItemText
                    sx={{ position: 'relative', zIndex: 1 }}
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 500,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </motion.div>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position='fixed'
        color='transparent'
        elevation={scrolled ? 4 : 0}
        sx={{
          borderBottom: scrolled ? 'none' : 'none',
          backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease-in-out',
          py: scrolled ? 0 : 0.5,
          zIndex: 1100
        }}>
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: { xs: 2, md: 4 },
            py: scrolled ? 0.5 : 1,
            pt: scrolled ? 0.5 : `calc(8px + env(safe-area-inset-top))`,
            transition: 'all 0.3s ease-in-out'
          }}>
          <Link to='/' style={{ textDecoration: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={Logo}
                alt='Logo'
                style={{
                  height: scrolled ? 36 : 42,
                  transition: 'all 0.3s ease-in-out',
                  filter: scrolled ? 'none' : 'brightness(0) invert(1)'
                }}
              />
            </Box>
          </Link>

          {isMobile ? (
            <IconButton
              edge='end'
              color='inherit'
              aria-label='menu'
              onClick={handleDrawerToggle}
              sx={{
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -6,
                  borderRadius: '50%',
                  border: '1px solid rgba(0,0,0,0.1)',
                  transform: mobileOpen ? 'scale(1)' : 'scale(0.8)',
                  opacity: mobileOpen ? 1 : 0,
                  transition: 'all 0.25s ease'
                }
              }}>
              <Hamburger open={mobileOpen} scrolled={scrolled} />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.label}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: scrolled ? 'black' : 'white',
                      textTransform: 'none',
                      fontWeight: isActive ? 600 : 500,
                      fontSize: '0.875rem',
                      letterSpacing: '0.02em',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      position: 'relative',
                      transition: 'all 0.2s ease-in-out',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: isActive ? '100%' : 0,
                        height: '2px',
                        backgroundColor: scrolled ? 'black' : 'white',
                        transition: 'width 0.3s ease-in-out, background-color 0.3s ease-in-out'
                      },
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        backgroundColor: scrolled ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)'
                      },
                      '&:hover::after': {
                        width: '100%'
                      }
                    }}>
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor='right'
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'white'
          }
        }}>
        <motion.div
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}>
          {drawer}
        </motion.div>
      </Drawer>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,.6), rgba(0,0,0,.2))',
              zIndex: 1099
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default Header;
